import apiPermissions from './constants/api-permissions.json';

/**
 * Resolve an API method constant to its request path, substituting path parameters.
 *
 * Moved here from `ui/dashboard/src/services/api.config.ts` so the generated services
 * can resolve their own paths without depending on an app.
 *
 * The `/api/v1` prefix is stripped because every transport's base URL already
 * includes it.
 *
 * @param apiMethod - An API method constant, e.g. `API_METHODS.UserRoleService.AddUserRole`
 * @param pathParams - Path parameter values, e.g. `{ user_id: '123' }`
 * @returns The path with parameters substituted, e.g. `/users/123/roles`
 * @throws If the method has no registered HTTP path
 *
 * @example
 * ```typescript
 * // For path: /users/{user_id}/roles
 * getApiPath(API_METHODS.UserRoleService.AddUserRole, { user_id: '123' });
 * // → '/users/123/roles'
 * ```
 */
export const getApiPath = <T extends Record<string, unknown> = Record<string, never>>(
  apiMethod: string,
  pathParams?: T,
): string => {
  const methodInfo = (apiPermissions as Record<string, { httpPath?: string }>)[apiMethod];
  if (!methodInfo?.httpPath) {
    throw new Error(`No HTTP path found for API method: ${apiMethod}`);
  }

  let path = methodInfo.httpPath.replace('/api/v1', '');

  if (pathParams) {
    Object.entries(pathParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        path = path.replace(`{${key}}`, String(value));
      }
    });
  }

  return path;
};
